import moment from "moment";
import * as CryptoJS from "crypto-js";
import { decode as base64_decode, encode as base64_encode } from "base-64";

import currency from "currency.js";
import { AMOUNT_API_URL } from "config";

const defaultDateFormat = "MM-DD-YYYY";
const defaultTimeFormat = "hh:mm A";

const formatDate = (date: string | Date | null): string | null => {
  return date && date !== "" ? moment(date).format(defaultDateFormat) : null;
};

const setFormatTime = (time: string | null): string | null => {
  return time && time !== ""
    ? moment(time, "HH:mm:ss").format(defaultTimeFormat)
    : null;
};

const defaultValue = <
  T extends { value: string | number; label: string },
  V extends string | number | (string | number)[] | null | undefined
>(
  option: T[] = [],
  value: V
): T | T[] | "" => {
  if (value != null) {
    if (typeof value === "string" || typeof value === "number") {
      return option ? option.find((opt) => opt.value === value) || "" : "";
    } else {
      return option
        ? option.filter((opt) => value.includes(opt.value.toString()))
        : "";
    }
  } else {
    return "";
  }
};

interface EncryptedData {
  ciphertext: string;
  salt: string;
}

const encryptValues = (text: string): EncryptedData => {
  const data = [{ text }];
  const cipherText = CryptoJS.AES.encrypt(
    JSON.stringify(data),
    "12345678901234561234567890123456"
  );
  return {
    ciphertext: cipherText.toString(),
    salt: "", // You might need to retrieve and store the salt for decryption
  };
};

const decryptValues = (encryptedData: EncryptedData): string => {
  const bytes = CryptoJS.AES.decrypt(
    encryptedData.ciphertext,
    "12345678901234561234567890123456"
  );
  const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  return decryptedData[0].text;
};

const encryptIDValues = (text: string) => {
  var data = [{ text }];
  const cipherText = CryptoJS.AES.encrypt(
    JSON.stringify(data),
    "12345678901234561234567890123456"
  );
  return base64_encode(cipherText.toString());
};

const decryptIdValues = (text: any) => {
  try {
    return (
      JSON.parse(
        CryptoJS.AES.decrypt(
          base64_decode(text),
          "12345678901234561234567890123456"
        ).toString(CryptoJS.enc.Utf8)
      )[0].text || 0
    );
  } catch (error) {
    return 0;
  }
};

const calculatePrice = (price: number, countryId: number, margin: string) => {
  try {
    let finalPrice;
    if (countryId === 101 && margin) {
      finalPrice = price * (1 + parseFloat(margin) / 100);
      return currency(finalPrice, { symbol: "₹" }).format();
    } else {
      finalPrice = price * (1 + parseFloat(margin) / 100) * 1.18;
      const priceInUSD = convertINRtoUSD(finalPrice); // using await here
      return currency(String(priceInUSD), { symbol: "$" }).format();
    }
  } catch (error) {
    console.error("Error calculating price:", error);
    return currency(price, { symbol: "$" }).format(); // Fallback to default currency format
  }
};

const calculatePriceValue = (price: number, countryId: number) => {
  try {
    if (countryId === 101) {
      return currency(price, { symbol: "₹" }).format();
    } else {
      const finalPrice = price * 1.18;
      const priceInUSD = convertINRtoUSD(finalPrice); // using await here
      return currency(String(priceInUSD), { symbol: "$" }).format();
    }
  } catch (error) {
    console.error("Error calculating price:", error);
    return currency(price, { symbol: "$" }).format(); // Fallback to default currency format
  }
};

// const convertINRtoUSD = async (amount: number): Promise<string> => {
//   try {
//     const response = await axios.get("https://open.er-api.com/v6/latest/USD");
//     const exchangeRate: number = response.data.rates.INR;
//     return (parseFloat(amount.toString()) / exchangeRate).toFixed(2);
//   } catch (error) {
//     console.error("Error converting INR to USD:", error);
//     return "0.00";
//   }
// };

const convertINRtoUSD = (amount: number): string => {
  try {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", AMOUNT_API_URL, false); // false makes the request synchronous
    xhr.send();
    if (xhr.status === 200) {
      const response = JSON.parse(xhr.responseText);
      const exchangeRate: number = response.rates.INR;
      return (parseFloat(amount.toString()) / exchangeRate).toFixed(2);
    } else {
      console.error("Failed to fetch exchange rate:", xhr.statusText);
      return "0.00";
    }
  } catch (error) {
    console.error("Error converting INR to USD:", error);
    return "0.00";
  }
};

const getCsrfToken = (): string | null => {
  const name = "csrfToken";
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(";");

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      // Extract only the value part of the CSRF token
      const tokenValue = c.substring(name.length + 1, c.length);
      // Remove any additional characters or whitespace
      return tokenValue.trim();
    }
  }
  return null;
};

export {
  formatDate,
  setFormatTime,
  defaultValue,
  encryptValues,
  decryptValues,
  encryptIDValues,
  decryptIdValues,
  calculatePrice,
  calculatePriceValue,
  getCsrfToken,
};
